import React, { useState } from "react";
import { Appointment } from "@myapp/contract";
import { getAppointmentStatusLabel, getAppointmentStatusColor } from "../../../entities/appointment";
import { formatDateDDMMYYYY } from "../../../shared/helpers/date";
import { ChevronDownIcon, ChevronUpIcon } from "lucide-react";

interface Props {
  appointments: Appointment[];
}

export const PreviousAppointmentsList: React.FC<Props> = ({ appointments }) => {
  const [open, setOpen] = useState(false);

  if (appointments.length === 0) return null;

  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 text-sm font-medium text-gray-700"
        onClick={() => setOpen((v) => !v)}
      >
        <span>Vorherige Termine ({appointments.length})</span>
        {open ? (
          <ChevronUpIcon className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronDownIcon className="w-4 h-4 text-gray-500" />
        )}
      </button>

      {open && (
        <ul className="divide-y divide-gray-100 bg-white">
          {appointments.map((a) => {
            const statusColor = getAppointmentStatusColor(a.status);
            const gewerk = a.workOrder?.external.gewerkeText;
            const hasActualDurations = a.actualWorkDuration != null || a.actualTravelDuration != null;
            return (
              <li key={a.id} className="px-4 py-3 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    Termin {a.sequenceNumber} · {formatDateDDMMYYYY(a.plannedStart)}
                  </span>
                  <span
                    className="inline-block px-2 py-0.5 rounded-full text-xs font-medium"
                    style={{
                      color: statusColor,
                      backgroundColor: statusColor + "20",
                      border: `1px solid ${statusColor}40`,
                    }}
                  >
                    {getAppointmentStatusLabel(a.status)}
                  </span>
                </div>

                {gewerk && (
                  <div className="text-xs text-gray-600 font-medium">{gewerk}</div>
                )}

                {a.status === 'COMPLETED_WITH_FOLLOW_UP' && a.followUpReason && (
                  <div className="text-xs px-2 py-1.5 rounded-md border border-[#ED9E42]/40 bg-[#ED9E42]/5 text-gray-700">
                    <span className="font-medium text-gray-500">Begründung:</span> {a.followUpReason}
                  </div>
                )}

                {hasActualDurations && (
                  <div className="text-xs text-gray-400">
                    Ist: Anfahrt {a.actualTravelDuration ?? 0} Min. · Arbeit {a.actualWorkDuration ?? 0} Min.
                    {a.actualDepartureDuration != null && ` · Abfahrt ${a.actualDepartureDuration} Min.`}
                  </div>
                )}

                {a.description && (
                  <div className="text-xs text-gray-500 italic">{a.description}</div>
                )}

                {a.technicianComment && (
                  <div className="text-xs text-gray-500 border-l-2 border-gray-200 pl-2">
                    {a.technicianComment}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
